import { useEffect, useState } from "react";
import { X, Plus, Tag } from "lucide-react";
import type { InterviewQuestion, InterviewCategory, QuestionCreate, QuestionUpdate } from "../api/InterviewQaApi";
import RichTextEditor from "shared/RichTextEditor";
import { isHtmlEmpty } from "shared/htmlUtils";

interface QuestionFormProps {
  categories: InterviewCategory[];
  initial?: InterviewQuestion | null;
  defaultCategoryId?: number | null;
  onSave: (data: QuestionCreate | QuestionUpdate) => Promise<void>;
  onClose: () => void;
}

const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Easy", color: "text-emerald-400" },
  { value: "medium", label: "Medium", color: "text-yellow-400" },
  { value: "hard", label: "Hard", color: "text-destructive" },
];

export default function QuestionForm({ categories, initial, defaultCategoryId, onSave, onClose }: QuestionFormProps) {
  const [question, setQuestion] = useState(initial?.question ?? "");
  const [answer, setAnswer] = useState(initial?.answer ?? "");
  const [categoryId, setCategoryId] = useState<number | "">(initial?.category_id ?? defaultCategoryId ?? "");
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">(initial?.difficulty ?? "medium");
  const [status, setStatus] = useState<"unanswered" | "answered">(initial?.status ?? "unanswered");
  const [source, setSource] = useState(initial?.source ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isHtmlEmpty(answer)) setStatus("answered");
    else setStatus("unanswered");
  }, [answer]);

  function addTag() {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  }

  function removeTag(t: string) {
    setTags(tags.filter((x) => x !== t));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (isHtmlEmpty(question)) errs.question = "Question is required";
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    try {
      await onSave({
        question,
        answer: isHtmlEmpty(answer) ? undefined : answer,
        category_id: categoryId !== "" ? Number(categoryId) : null,
        difficulty,
        status,
        tags,
        source: source.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground font-inter">
            {initial ? "Edit Question" : "Add Question"}
          </h2>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Question */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Question *</label>
            <RichTextEditor
              defaultValue={question}
              onChange={setQuestion}
              placeholder="Enter the interview question…"
            />
            {errors.question && <p className="text-xs text-destructive mt-1">{errors.question}</p>}
          </div>

          {/* Answer */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Answer</label>
            <RichTextEditor
              defaultValue={answer}
              onChange={setAnswer}
              placeholder="Write a detailed answer… (leave blank to mark as unanswered)"
              className="rich-text-editor--tall"
            />
          </div>

          {/* Category + Difficulty row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Category</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary font-inter"
              >
                <option value="">Uncategorised</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Difficulty</label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as "easy" | "medium" | "hard")}
                className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary font-inter"
              >
                {DIFFICULTY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Tags</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {tags.map((t) => (
                <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-mono border border-primary/30">
                  <Tag className="w-3 h-3" />
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="ml-0.5 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                className="flex-1 bg-surface-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-mono"
                placeholder="Add a tag and press Enter"
              />
              <button type="button" onClick={addTag} className="px-3 py-2 bg-muted hover:bg-border text-foreground rounded-xl text-sm">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Source */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Source / Interview Context</label>
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="e.g. XYZ Company, 2nd round, June 2025"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Additional Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary resize-none font-inter"
              placeholder="Any extra context or hints…"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-xl hover:bg-surface-muted font-inter">
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={saving}
            className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium rounded-xl font-inter"
          >
            {saving ? "Saving…" : initial ? "Save Changes" : "Add Question"}
          </button>
        </div>
      </div>
    </div>
  );
}
